import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Moment from "react-moment";
import {
  getData,
} from "../../../../../Services/Services";
import {
  getSopMasterData,
} from "../../../../../Services/EndPoints";
import Loader from "react-spinner-loader";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
 
import SopsInstanceExpandableTable from "./SopsInstanceExpandableTable";
 
// Session storage helper functions
const storageHelpers = {
  // Save data with expiration time
  setWithExpiry: (key, value, expiryInHours) => {
    const now = new Date();
    const item = {
      value: value,
      expiry: now.getTime() + expiryInHours * 60 * 60 * 1000, // Convert hours to milliseconds
    };
    sessionStorage.setItem(key, JSON.stringify(item));
  },
 
  // Get data and check if it's expired
  getWithExpiry: (key) => {
    const itemStr = sessionStorage.getItem(key);
    if (!itemStr) return null;
   
    const item = JSON.parse(itemStr);
    const now = new Date();
   
    // Compare the expiry time with the current time
    if (now.getTime() > item.expiry) {
      // If expired, remove the item from storage
      sessionStorage.removeItem(key);
      return null;
    }
    return item.value;
  }
};
 
const SopsInstanceTable = () => {
  const { t } = useTranslation();
  const [userTaskData, setUserTaskData] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loader, setLoader] = useState(false);
 
  // State to store the SOP instance data
  const [sopInstanceData, setSopInstanceData] = useState(null);
  const [sopInstanceId, setSopInstanceId] = useState(null);
 
  const navigate = useNavigate();
  const { state } = useLocation();
 
  // Session storage key
  const SESSION_STORAGE_KEY = "sops_instance_table_data";
  // Expiration time in hours
  const EXPIRY_HOURS = 2;
 
  useEffect(() => {
    // Retrieve data - first check state, then session storage
    let stateData = state?.data;
   
    // If state data exists, save it to session storage with expiry
    if (stateData) {
      storageHelpers.setWithExpiry(SESSION_STORAGE_KEY, stateData, EXPIRY_HOURS);
      setSopInstanceData(stateData);
      setSopInstanceId(stateData.id);
    }
    // If no state data, try to get from session storage
    else {
      const storedData = storageHelpers.getWithExpiry(SESSION_STORAGE_KEY);
      if (storedData) {
        setSopInstanceData(storedData);
        setSopInstanceId(storedData.id);
      } else {
        // No data in state or session storage (or data expired), redirect to list view
        toast.error("No SOP instance data found or data expired. Redirecting to view page.", {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
        setTimeout(() => navigate("../viewsops-instance"), 2000);
      }
    }
  }, [state, navigate]);
 
 
  useEffect(() => {
    // Only fetch task list when sopInstanceId is available
    if (sopInstanceId) {
      getTaskList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sopInstanceId]);
 
 
  const getTaskList = async () => {
    setLoader(true);
    try {
      const masterUserTaskListResponse = await getData(
        getSopMasterData + sopInstanceId
      );
     
      const masterUserTaskList = masterUserTaskListResponse || [];
 
      const formattedData = masterUserTaskList.map(incident => ({
        taskName: incident.taskName,
        taskType: incident.taskType,
        createdOn: incident.createdOn,
        taskDescription: incident.taskDescription,
        id: incident.id,
        // Add this to map the child table data
        userTaskMapList: incident.sopTaskInstanceEntityList.map(task => ({
          ...task,
          parent_task_type: incident.taskType || "341" // Pass parent task type to children
        }))
      }));
 
      setUserTaskData(formattedData);
      setRowCount(masterUserTaskList.length);
    } catch (error) {
      console.error("Error fetching task list:", error);
      toast.error(t("fetch_error_message"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setLoader(false);
    }
  };
 
  const columns = React.useMemo(
    () => [
      {
        Header: t("Task Name"),
        accessor: "taskName",
        disableSortBy: true,
      },
      {
        Header: t("Task Type"),
        accessor: "taskType",
        disableSortBy: true,
        Cell: (props) => {
          return props.value === "341"
            ? "Activity"
            : props.value === "340"
            ? "Notification"
            : props.value; // Default to showing the original value if it's not 341 or 340
        },        
      },
      {
        Header: t("Created On"),
        accessor: "createdOn",
        disableSortBy: true,
        Cell: (props) => (
          <Moment format="DD-MM-YYYY, hh:mm A">{props.value}</Moment>
        ),
      },
      {
        Header: t("Task Description"),
        accessor: "taskDescription",
        disableSortBy: true,
        Cell: ({ value }) => (value?.trim() ? value : "NA"),
      },
    ],
      // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, navigate]
  );

 
  if (!sopInstanceData) {
    return <Loader show={true} type="body" message="Loading Data" />;
  }
 
  return (
    <div>
      <Loader show={loader} type="body" message="Loading Data" />
 
      <SopsInstanceExpandableTable
        data={userTaskData}
        columns={columns}
        numberOfRows={7}
        showExport={false}
        showFilter={false}
        showReset={false}
        onExpand={false}
        rowCount={rowCount}
        headerTableHeadStyle={{ background: "#1a7a73", color: "#FFFFFF" }}
        tableStyle={{
          border: "1px solid #f0f0f0",
          borderRadius: "12px",
          padding: "0px"
        }}
      />
    </div>
  );
};
 
export default SopsInstanceTable;